import React, { useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface DebugQuestGameProps {
  isOpen: boolean;
  onClose: () => void;
}

const DebugQuestGame: React.FC<DebugQuestGameProps> = ({ isOpen, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<any>(null);
  const animationRef = useRef<number>(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const keysRef = useRef<Record<string, boolean>>({});

  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, []);

  const playSound = useCallback((type: string) => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'suspended') return;
    
    const osc = audioCtxRef.current.createOscillator();
    const gain = audioCtxRef.current.createGain();
    osc.connect(gain);
    gain.connect(audioCtxRef.current.destination);
    
    const now = audioCtxRef.current.currentTime;
    gain.gain.setValueAtTime(0.12, now);
    
    switch (type) {
      case 'attack':
        osc.type = 'square';
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.linearRampToValueAtTime(110, now + 0.1);
        gain.gain.linearRampToValueAtTime(0, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      case 'hit':
        osc.type = 'square';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.linearRampToValueAtTime(150, now + 0.05);
        gain.gain.linearRampToValueAtTime(0, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      case 'bossHit':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.linearRampToValueAtTime(100, now + 0.15);
        gain.gain.linearRampToValueAtTime(0, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      case 'hurt':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.linearRampToValueAtTime(80, now + 0.2);
        gain.gain.linearRampToValueAtTime(0, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;
      case 'item':
        osc.type = 'square';
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.setValueAtTime(550, now + 0.1);
        osc.frequency.setValueAtTime(660, now + 0.2);
        gain.gain.linearRampToValueAtTime(0, now + 0.3);
        osc.start(now);
        osc.stop(now + 0.3);
        break;
      case 'door':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(330, now);
        osc.frequency.setValueAtTime(440, now + 0.05);
        gain.gain.linearRampToValueAtTime(0, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      case 'enemyDeath':
        osc.type = 'square';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(50, now + 0.25);
        gain.gain.linearRampToValueAtTime(0, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
        break;
      case 'shoot':
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.linearRampToValueAtTime(300, now + 0.1);
        gain.gain.linearRampToValueAtTime(0, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      case 'victory':
        osc.type = 'square';
        [523, 659, 784, 1047].forEach((freq, i) => {
          osc.frequency.setValueAtTime(freq, now + i * 0.15);
        });
        gain.gain.linearRampToValueAtTime(0, now + 0.7);
        osc.start(now);
        osc.stop(now + 0.7);
        break;
      case 'gameover':
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.linearRampToValueAtTime(50, now + 0.5);
        gain.gain.linearRampToValueAtTime(0, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.5);
        break;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SCALE = 2;
    const WIDTH = canvas.width / SCALE;
    const HEIGHT = canvas.height / SCALE;
    const TILE = 16;

    // Colors
    const C = {
      black: '#0a0a0a',
      darkGray: '#1a1a2e',
      gray: '#2d2d44',
      lightGray: '#4a4a5c',
      white: '#e8e8e8',
      floor1: '#2a2a3a',
      floor2: '#252535',
      wall: '#1a1a28',
      wallTop: '#333344',
      wallHighlight: '#404055',
      door: '#3a2a1a',
      doorHighlight: '#5a4a3a',
      skin: '#8d5524',
      skinDark: '#6b4423',
      hair: '#1a1a1a',
      shirt: '#5dade2',
      shirtDark: '#3498db',
      pants: '#34495e',
      enemy: '#e74c3c',
      enemyDark: '#c0392b',
      enemyLight: '#ff6b5b',
      scopeCreep: '#e67e22',
      scopeCreepDark: '#d35400',
      raceCond: '#3498db',
      raceCondDark: '#2980b9',
      bug: '#9b59b6',
      bugDark: '#8e44ad',
      bugLight: '#bb79d6',
      item: '#f1c40f',
      itemDark: '#d4ac0d',
      health: '#e74c3c',
      healthFull: '#ff6b6b',
      healthEmpty: '#4a2a2a',
      text: '#e8e8e8',
      textDim: '#888',
      success: '#2ecc71',
      coffee: '#6f4e37',
      coffeeCup: '#f5f5dc',
    };

    // Game state
    const game = {
      state: 'title' as 'title' | 'playing' | 'message' | 'gameover' | 'victory',
      room: 'office',
      player: {
        x: 128, y: 160, w: 14, h: 14, speed: 2.5, dir: 'down' as string,
        health: 6, maxHealth: 6, attacking: false, attackFrame: 0,
        attackCooldown: 0, invincible: 0, hasSword: false, hasCoffee: false, moveFrame: 0,
      },
      enemies: [] as any[],
      items: [] as any[],
      projectiles: [] as any[],
      particles: [] as any[],
      currentMessage: null as string | null,
      roomsCleared: {} as Record<string, boolean>,
      doorsUnlocked: {} as Record<string, boolean>,
      frame: 0,
      shakeFrames: 0,
    };
    gameRef.current = game;

    // Room definitions
    const ROOMS: Record<string, any> = {
      office: {
        name: "Your Desk", width: 16, height: 14,
        playerStart: { x: 128, y: 160 }, tiles: null, enemies: [],
        items: [{ type: 'sword', x: 200, y: 80, message: "Found your KEYBOARD!\nIt's dangerous to code alone." }],
        doors: [{ x: 7, y: 0, to: 'hallway', toX: 7, toY: 12, dir: 'up' }],
        firstVisit: "Monday morning.\nThere's a bug in production.\nTime to hunt it down.",
      },
      hallway: {
        name: "The Hallway", width: 16, height: 14,
        playerStart: { x: 128, y: 180 },
        enemies: [{ type: 'nullpointer', x: 60, y: 80 }, { type: 'nullpointer', x: 180, y: 100 }],
        items: [],
        doors: [
          { x: 7, y: 13, to: 'office', toX: 7, toY: 1, dir: 'down' },
          { x: 0, y: 6, to: 'breakroom', toX: 14, toY: 6, dir: 'left' },
          { x: 7, y: 0, to: 'serverroom', toX: 7, toY: 12, dir: 'up' },
        ],
        firstVisit: "Watch out for NULL POINTERS.\nThey'll crash your day.",
      },
      breakroom: {
        name: "Break Room", width: 16, height: 14,
        playerStart: { x: 200, y: 100 },
        enemies: [{ type: 'scopecreep', x: 80, y: 80 }],
        items: [{ type: 'coffee', x: 40, y: 40, message: "COFFEE acquired!\nHealth restored.\n+2 max HP" }],
        doors: [{ x: 15, y: 6, to: 'hallway', toX: 1, toY: 6, dir: 'right' }],
        firstVisit: "The break room.\nSomeone left coffee!\nWatch for SCOPE CREEP.",
      },
      serverroom: {
        name: "Server Room", width: 16, height: 14,
        playerStart: { x: 128, y: 180 },
        enemies: [
          { type: 'nullpointer', x: 60, y: 60 },
          { type: 'nullpointer', x: 180, y: 60 },
          { type: 'racecondition', x: 120, y: 100 },
        ],
        items: [{ type: 'health', x: 200, y: 40 }],
        doors: [
          { x: 7, y: 13, to: 'hallway', toX: 7, toY: 1, dir: 'down' },
          { x: 7, y: 0, to: 'production', toX: 7, toY: 12, locked: true, dir: 'up' },
        ],
        firstVisit: "The server room. Cold.\nRACE CONDITIONS lurk here.\nFind the bug's source.",
      },
      production: {
        name: "PRODUCTION", width: 16, height: 14,
        playerStart: { x: 128, y: 180 }, enemies: [], items: [],
        doors: [{ x: 7, y: 13, to: 'serverroom', toX: 7, toY: 1, dir: 'down' }],
        boss: { type: 'thebug', x: 112, y: 50 },
        firstVisit: "This is it.\nThe bug that's been crashing prod.\nTime to squash it.",
      },
    };

    // Helper functions
    const rectsOverlap = (a: any, b: any) => {
      return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
    };

    const collidesWithWall = (x: number, y: number, w: number, h: number, room: any) => {
      const tiles = room.tiles;
      const margin = 2;
      const points = [
        { x: x + margin, y: y + margin },
        { x: x + w - margin, y: y + margin },
        { x: x + margin, y: y + h - margin },
        { x: x + w - margin, y: y + h - margin },
      ];
      for (const pt of points) {
        const tileX = Math.floor(pt.x / TILE);
        const tileY = Math.floor(pt.y / TILE);
        if (tileX < 0 || tileX >= room.width || tileY < 0 || tileY >= room.height) return true;
        if (tiles[tileY] && tiles[tileY][tileX] === 1) return true;
      }
      return false;
    };

    const generateRoom = (room: any) => {
      const w = room.width;
      const h = room.height;
      const tiles: number[][] = [];
      for (let y = 0; y < h; y++) {
        tiles[y] = [];
        for (let x = 0; x < w; x++) {
          if (y === 0 || y === h - 1 || x === 0 || x === w - 1) {
            tiles[y][x] = 1;
          } else {
            tiles[y][x] = ((x + y) % 4 === 0) ? 3 : 0;
          }
        }
      }
      if (room.doors) {
        room.doors.forEach((door: any) => {
          for (let i = -1; i <= 1; i++) {
            if (door.dir === 'up' || door.dir === 'down') {
              if (door.x + i >= 0 && door.x + i < w) tiles[door.y][door.x + i] = 2;
            } else {
              if (door.y + i >= 0 && door.y + i < h) tiles[door.y + i][door.x] = 2;
            }
          }
        });
      }
      return tiles;
    };

    const spawnEnemy = (type: string, x: number, y: number) => {
      const base = {
        x, y, w: 14, h: 14, type, hp: 2, maxHp: 2, speed: 0.8, damage: 1,
        stateTimer: 0, flash: 0, knockback: { x: 0, y: 0 }, wanderDir: 0,
        isBoss: false, phase: 1,
      };
      switch (type) {
        case 'nullpointer': return { ...base, hp: 2, maxHp: 2, speed: 0.7 };
        case 'scopecreep': return { ...base, hp: 3, maxHp: 3, speed: 0.4, w: 18, h: 18 };
        case 'racecondition': return { ...base, hp: 2, maxHp: 2, speed: 1.8 };
        case 'thebug': return { ...base, hp: 12, maxHp: 12, speed: 0.8, w: 28, h: 28, isBoss: true, phase: 1 };
        default: return base;
      }
    };

    const spawnItem = (type: string, x: number, y: number, message: string = '') => {
      return { type, x, y, w: 12, h: 12, collected: false, message };
    };

    const showMessage = (text: string) => {
      game.currentMessage = text;
      game.state = 'message';
    };

    const loadRoom = (roomId: string, entryX?: number, entryY?: number) => {
      const room = ROOMS[roomId];
      if (!room) return;
      game.room = roomId;
      room.tiles = generateRoom(room);
      if (entryX !== undefined && entryY !== undefined) {
        game.player.x = entryX * TILE + 1;
        game.player.y = entryY * TILE + 1;
      } else {
        game.player.x = room.playerStart.x;
        game.player.y = room.playerStart.y;
      }
      game.enemies = [];
      if (!game.roomsCleared[roomId]) {
        if (room.enemies) room.enemies.forEach((e: any) => game.enemies.push(spawnEnemy(e.type, e.x, e.y)));
        if (room.boss) game.enemies.push(spawnEnemy(room.boss.type, room.boss.x, room.boss.y));
      }
      game.items = [];
      if (room.items) {
        room.items.forEach((item: any) => {
          const itemId = `${roomId}_${item.type}_${item.x}_${item.y}`;
          if (!game.roomsCleared[itemId]) game.items.push(spawnItem(item.type, item.x, item.y, item.message));
        });
      }
      game.projectiles = [];
      if (room.firstVisit && !game.roomsCleared[`${roomId}_visited`]) {
        game.roomsCleared[`${roomId}_visited`] = true;
        showMessage(room.firstVisit);
      }
    };

    const startGame = () => {
      game.state = 'playing';
      game.player = {
        x: 128, y: 160, w: 14, h: 14, speed: 2.5, dir: 'down',
        health: 6, maxHealth: 6, attacking: false, attackFrame: 0,
        attackCooldown: 0, invincible: 0, hasSword: false, hasCoffee: false, moveFrame: 0,
      };
      game.roomsCleared = {};
      game.doorsUnlocked = {};
      game.enemies = [];
      game.items = [];
      game.projectiles = [];
      game.particles = [];
      game.frame = 0;
      loadRoom('office');
    };

    const getAttackHitbox = (p: typeof game.player) => {
      const reach = 20;
      switch (p.dir) {
        case 'up': return { x: p.x - 4, y: p.y - reach, w: p.w + 8, h: reach };
        case 'down': return { x: p.x - 4, y: p.y + p.h, w: p.w + 8, h: reach };
        case 'left': return { x: p.x - reach, y: p.y - 4, w: reach, h: p.h + 8 };
        case 'right': return { x: p.x + p.w, y: p.y - 4, w: reach, h: p.h + 8 };
        default: return { x: p.x, y: p.y, w: p.w, h: p.h };
      }
    };

    const damageEnemy = (enemy: any, amount: number, dir: string) => {
      enemy.hp -= amount;
      enemy.flash = 12;
      const kb = enemy.isBoss ? 3 : 8;
      switch (dir) {
        case 'up': enemy.knockback.y = -kb; break;
        case 'down': enemy.knockback.y = kb; break;
        case 'left': enemy.knockback.x = -kb; break;
        case 'right': enemy.knockback.x = kb; break;
      }
      playSound(enemy.isBoss ? 'bossHit' : 'hit');
      game.shakeFrames = enemy.isBoss ? 6 : 3;
    };

    const damagePlayer = (amount: number) => {
      game.player.health -= amount;
      game.player.invincible = 60;
      game.shakeFrames = 10;
      playSound('hurt');
      if (game.player.health <= 0) {
        game.state = 'gameover';
        playSound('gameover');
      }
    };

    // Drawing functions
    const drawPlayer = (x: number, y: number, dir: string, frame: number, attacking: boolean, attackFrame: number) => {
      const bobY = Math.sin(frame * 0.3) * 1;
      ctx.save();
      ctx.translate(x + 7, y + 7);

      // Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(0, 6, 5, 2, 0, 0, Math.PI * 2);
      ctx.fill();

      // Attack swing
      if (attacking) {
        ctx.strokeStyle = C.item;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        const swingProgress = attackFrame / 12;
        let startAngle: number, endAngle: number;
        switch(dir) {
          case 'up': startAngle = Math.PI; endAngle = 0; break;
          case 'down': startAngle = 0; endAngle = Math.PI; break;
          case 'left': startAngle = Math.PI/2; endAngle = -Math.PI/2; break;
          case 'right': default: startAngle = -Math.PI/2; endAngle = Math.PI/2; break;
        }
        ctx.beginPath();
        ctx.arc(0, 0, 12, startAngle, startAngle + (endAngle - startAngle) * swingProgress);
        ctx.stroke();
      }

      // Body
      ctx.fillStyle = C.shirt;
      ctx.fillRect(-4, -2 + bobY, 8, 7);
      ctx.fillStyle = C.shirtDark;
      ctx.fillRect(-4, 3 + bobY, 8, 2);

      // Pants
      ctx.fillStyle = C.pants;
      ctx.fillRect(-3, 5 + bobY, 3, 3);
      ctx.fillRect(0, 5 + bobY, 3, 3);

      // Head
      ctx.fillStyle = C.skin;
      ctx.fillRect(-3, -6 + bobY, 6, 5);

      // Hair
      ctx.fillStyle = C.hair;
      ctx.fillRect(-3, -7 + bobY, 6, 2);
      ctx.fillRect(-4, -6 + bobY, 1, 3);
      ctx.fillRect(3, -6 + bobY, 1, 3);

      // Face
      ctx.fillStyle = '#222';
      switch(dir) {
        case 'down':
          ctx.fillRect(-2, -4 + bobY, 1, 1);
          ctx.fillRect(1, -4 + bobY, 1, 1);
          break;
        case 'up':
          ctx.fillStyle = C.hair;
          ctx.fillRect(-3, -6 + bobY, 6, 4);
          break;
        case 'left':
          ctx.fillRect(-3, -4 + bobY, 1, 1);
          break;
        case 'right':
          ctx.fillRect(2, -4 + bobY, 1, 1);
          break;
      }

      // Arms
      ctx.fillStyle = C.skin;
      if (attacking) {
        switch(dir) {
          case 'up': ctx.fillRect(-1, -8 + bobY, 2, 3); break;
          case 'down': ctx.fillRect(-1, 5 + bobY, 2, 3); break;
          case 'left': ctx.fillRect(-8, -1 + bobY, 4, 2); break;
          case 'right': ctx.fillRect(4, -1 + bobY, 4, 2); break;
        }
      } else {
        ctx.fillRect(-6, -1 + bobY, 2, 2);
        ctx.fillRect(4, -1 + bobY, 2, 2);
      }

      // Sword
      if (attacking && attackFrame > 2 && attackFrame < 10) {
        ctx.fillStyle = C.item;
        switch(dir) {
          case 'up':
            ctx.fillRect(-1, -18 + bobY, 2, 10);
            ctx.fillStyle = '#888';
            ctx.fillRect(-2, -8 + bobY, 4, 2);
            break;
          case 'down':
            ctx.fillRect(-1, 8 + bobY, 2, 10);
            ctx.fillStyle = '#888';
            ctx.fillRect(-2, 6 + bobY, 4, 2);
            break;
          case 'left':
            ctx.fillRect(-18, -1 + bobY, 10, 2);
            ctx.fillStyle = '#888';
            ctx.fillRect(-8, -2 + bobY, 2, 4);
            break;
          case 'right':
            ctx.fillRect(8, -1 + bobY, 10, 2);
            ctx.fillStyle = '#888';
            ctx.fillRect(6, -2 + bobY, 2, 4);
            break;
        }
      }

      ctx.restore();
    };

    const drawNullPointer = (x: number, y: number, frame: number, flash: boolean) => {
      ctx.save();
      ctx.translate(x + 7, y + 7);
      const hover = Math.sin(frame * 0.1) * 2;
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(0, 8, 5, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = flash ? C.white : C.enemy;
      ctx.fillRect(-5, -4 + hover, 10, 12);
      ctx.fillStyle = flash ? C.enemy : C.enemyDark;
      ctx.fillRect(-5, -4 + hover + Math.floor(frame/4) % 12, 10, 2);
      ctx.fillStyle = C.white;
      ctx.fillRect(-3, -1 + hover, 2, 2);
      ctx.fillRect(1, -1 + hover, 2, 2);
      ctx.fillStyle = flash ? C.white : C.enemyLight;
      ctx.font = '4px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('NULL', 0, 6 + hover);
      ctx.restore();
    };

    const drawScopeCreep = (x: number, y: number, frame: number, flash: boolean) => {
      ctx.save();
      ctx.translate(x + 9, y + 9);
      const pulse = Math.sin(frame * 0.05) * 2;
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(0, 10, 8 + pulse/2, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = flash ? C.white : C.scopeCreep;
      ctx.beginPath();
      ctx.ellipse(0, 0, 8 + pulse, 8 + pulse, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = flash ? C.scopeCreep : C.scopeCreepDark;
      ctx.beginPath();
      ctx.ellipse(-2, -2, 3, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = C.white;
      ctx.beginPath();
      ctx.arc(-3, -3, 2, 0, Math.PI * 2);
      ctx.arc(2, -2, 2, 0, Math.PI * 2);
      ctx.arc(0, 3, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#222';
      ctx.fillRect(-3, -3, 1, 1);
      ctx.fillRect(2, -2, 1, 1);
      ctx.fillRect(0, 3, 1, 1);
      ctx.restore();
    };

    const drawRaceCondition = (x: number, y: number, frame: number, flash: boolean) => {
      ctx.save();
      ctx.translate(x + 7, y + 7);
      const flicker = Math.floor(frame / 2) % 2;
      const offsetX = flicker ? (Math.random() - 0.5) * 4 : 0;
      const offsetY = flicker ? (Math.random() - 0.5) * 4 : 0;
      ctx.translate(offsetX, offsetY);
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.beginPath();
      ctx.ellipse(0, 7, 4, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = flash ? C.white : C.raceCond;
      ctx.beginPath();
      ctx.moveTo(2, -6);
      ctx.lineTo(-3, 0);
      ctx.lineTo(0, 0);
      ctx.lineTo(-2, 6);
      ctx.lineTo(3, 0);
      ctx.lineTo(0, 0);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = C.white;
      for (let i = 0; i < 3; i++) {
        const sparkX = Math.cos(frame * 0.3 + i * 2) * 6;
        const sparkY = Math.sin(frame * 0.3 + i * 2) * 6;
        ctx.fillRect(sparkX - 1, sparkY - 1, 2, 2);
      }
      ctx.restore();
    };

    const drawBoss = (x: number, y: number, w: number, frame: number, flash: boolean, phase: number) => {
      ctx.save();
      ctx.translate(x + w/2, y + w/2);
      const breathe = Math.sin(frame * 0.05) * 2;
      const angry = phase === 2;

      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.ellipse(0, 14, 12 + breathe, 4, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = flash ? C.white : (angry ? '#c0392b' : C.bug);
      ctx.beginPath();
      ctx.ellipse(0, 2, 12 + breathe, 8 + breathe/2, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = flash ? C.bug : C.bugDark;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-8, 2); ctx.lineTo(8, 2);
      ctx.moveTo(-6, -2); ctx.lineTo(6, -2);
      ctx.moveTo(-6, 6); ctx.lineTo(6, 6);
      ctx.stroke();

      ctx.fillStyle = flash ? C.white : (angry ? '#e74c3c' : C.bugLight);
      ctx.beginPath();
      ctx.arc(0, -8, 6, 0, Math.PI * 2);
      ctx.fill();

      if (angry) {
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-6, -12); ctx.lineTo(-2, -10);
        ctx.moveTo(6, -12); ctx.lineTo(2, -10);
        ctx.stroke();
      }

      ctx.fillStyle = C.white;
      ctx.beginPath();
      ctx.arc(-3, -9, 3, 0, Math.PI * 2);
      ctx.arc(3, -9, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = angry ? '#c0392b' : '#222';
      ctx.fillRect(-3, -9, 2, 2);
      ctx.fillRect(2, -9, 2, 2);

      ctx.strokeStyle = flash ? C.white : C.bugDark;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-2, -13); ctx.lineTo(-4, -18 + Math.sin(frame * 0.2) * 2);
      ctx.moveTo(2, -13); ctx.lineTo(4, -18 + Math.sin(frame * 0.2 + 1) * 2);
      ctx.stroke();

      ctx.fillStyle = angry ? '#ff0' : C.bugLight;
      ctx.beginPath();
      ctx.arc(-4, -18 + Math.sin(frame * 0.2) * 2, 2, 0, Math.PI * 2);
      ctx.arc(4, -18 + Math.sin(frame * 0.2 + 1) * 2, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = flash ? C.white : C.bugDark;
      for (let i = 0; i < 3; i++) {
        const legOffset = Math.sin(frame * 0.15 + i) * 2;
        ctx.beginPath();
        ctx.moveTo(-10 - breathe, -2 + i * 5);
        ctx.lineTo(-16 - breathe, 2 + i * 5 + legOffset);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(10 + breathe, -2 + i * 5);
        ctx.lineTo(16 + breathe, 2 + i * 5 - legOffset);
        ctx.stroke();
      }

      ctx.fillStyle = flash ? C.white : C.bugDark;
      ctx.beginPath();
      ctx.moveTo(-4, -3); ctx.lineTo(-6, 0); ctx.lineTo(-3, -1);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(4, -3); ctx.lineTo(6, 0); ctx.lineTo(3, -1);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    };

    const drawSword = (x: number, y: number, frame: number) => {
      const bob = Math.sin(frame * 0.1) * 2;
      ctx.save();
      ctx.translate(x + 6, y + 6 + bob);
      ctx.fillStyle = `rgba(241, 196, 15, 0.3)`;
      ctx.beginPath();
      ctx.arc(0, 0, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ccc';
      ctx.beginPath();
      ctx.moveTo(0, -8); ctx.lineTo(2, -2); ctx.lineTo(2, 4); ctx.lineTo(-2, 4); ctx.lineTo(-2, -2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillRect(-1, -6, 1, 8);
      ctx.fillStyle = C.item;
      ctx.fillRect(-4, 4, 8, 2);
      ctx.fillStyle = '#654321';
      ctx.fillRect(-1, 6, 2, 4);
      ctx.fillStyle = C.item;
      ctx.beginPath();
      ctx.arc(0, 11, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    const drawCoffee = (x: number, y: number, frame: number) => {
      const bob = Math.sin(frame * 0.1) * 2;
      ctx.save();
      ctx.translate(x + 6, y + 6 + bob);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const steamY = -8 - Math.sin(frame * 0.1 + i) * 3;
        ctx.beginPath();
        ctx.moveTo(-2 + i * 2, -4);
        ctx.quadraticCurveTo(-3 + i * 2, steamY, -2 + i * 2 + Math.sin(frame * 0.1 + i), steamY - 3);
        ctx.stroke();
      }
      ctx.fillStyle = C.coffeeCup;
      ctx.fillRect(-4, -3, 8, 10);
      ctx.fillStyle = C.coffee;
      ctx.fillRect(-3, -2, 6, 6);
      ctx.fillStyle = '#fff';
      ctx.fillRect(-3, -3, 2, 8);
      ctx.strokeStyle = C.coffeeCup;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(6, 2, 3, -Math.PI/2, Math.PI/2);
      ctx.stroke();
      ctx.restore();
    };

    const drawHealth = (x: number, y: number, frame: number) => {
      const bob = Math.sin(frame * 0.1) * 2;
      const pulse = Math.sin(frame * 0.15) * 1 + 1;
      ctx.save();
      ctx.translate(x + 6, y + 6 + bob);
      ctx.fillStyle = 'rgba(231, 76, 60, 0.3)';
      ctx.beginPath();
      ctx.arc(0, 0, 8 + pulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = C.health;
      ctx.beginPath();
      ctx.moveTo(0, 3);
      ctx.bezierCurveTo(-5, -1, -5, -5, 0, -5);
      ctx.bezierCurveTo(5, -5, 5, -1, 0, 3);
      ctx.fill();
      ctx.fillStyle = C.healthFull;
      ctx.beginPath();
      ctx.arc(-2, -3, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    // Update functions
    const updatePlayer = () => {
      const p = game.player;
      const room = ROOMS[game.room];
      const keys = keysRef.current;

      if (p.invincible > 0) p.invincible--;
      if (p.attackCooldown > 0) p.attackCooldown--;

      if (p.attacking) {
        p.attackFrame++;
        if (p.attackFrame >= 12) {
          p.attacking = false;
          p.attackFrame = 0;
        }
        if (p.attackFrame === 4 && p.hasSword) {
          const hitbox = getAttackHitbox(p);
          game.enemies.forEach(enemy => {
            if (rectsOverlap(hitbox, enemy)) damageEnemy(enemy, 1, p.dir);
          });
        }
      }

      if (!p.attacking) {
        let dx = 0, dy = 0;
        if (keys['ArrowUp'] || keys['KeyW']) { dy = -1; p.dir = 'up'; }
        if (keys['ArrowDown'] || keys['KeyS']) { dy = 1; p.dir = 'down'; }
        if (keys['ArrowLeft'] || keys['KeyA']) { dx = -1; p.dir = 'left'; }
        if (keys['ArrowRight'] || keys['KeyD']) { dx = 1; p.dir = 'right'; }
        if (dx !== 0 && dy !== 0) { dx *= 0.707; dy *= 0.707; }
        if (dx !== 0 || dy !== 0) p.moveFrame++;

        const newX = p.x + dx * p.speed;
        const newY = p.y + dy * p.speed;
        if (!collidesWithWall(newX, p.y, p.w, p.h, room)) p.x = newX;
        if (!collidesWithWall(p.x, newY, p.w, p.h, room)) p.y = newY;
      }

      if ((keys['Space'] || keys['KeyZ']) && !p.attacking && p.attackCooldown === 0 && p.hasSword) {
        p.attacking = true;
        p.attackFrame = 0;
        p.attackCooldown = 20;
        playSound('attack');
      }

      p.x = Math.max(4, Math.min(p.x, room.width * TILE - p.w - 4));
      p.y = Math.max(4, Math.min(p.y, room.height * TILE - p.h - 4));
    };

    const updateEnemies = () => {
      const p = game.player;
      const room = ROOMS[game.room];

      game.enemies = game.enemies.filter(e => {
        if (e.hp <= 0) {
          for (let i = 0; i < 12; i++) {
            game.particles.push({
              x: e.x + e.w / 2, y: e.y + e.h / 2,
              vx: (Math.random() - 0.5) * 5, vy: (Math.random() - 0.5) * 5 - 2,
              life: 40, color: e.isBoss ? C.bug : C.enemy, size: e.isBoss ? 4 : 2,
            });
          }
          playSound('enemyDeath');
          if (e.isBoss) { game.state = 'victory'; playSound('victory'); }
          return false;
        }
        return true;
      });

      game.enemies.forEach(e => {
        if (e.flash > 0) e.flash--;
        if (e.knockback.x !== 0 || e.knockback.y !== 0) {
          e.x += e.knockback.x; e.y += e.knockback.y;
          e.knockback.x *= 0.8; e.knockback.y *= 0.8;
          if (Math.abs(e.knockback.x) < 0.1) e.knockback.x = 0;
          if (Math.abs(e.knockback.y) < 0.1) e.knockback.y = 0;
          return;
        }
        e.stateTimer++;
        const dx = p.x - e.x;
        const dy = p.y - e.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (e.isBoss) {
          if (e.hp <= e.maxHp / 2 && e.phase === 1) {
            e.phase = 2;
            e.speed = 1.2;
            showMessage("The bug is getting desperate!\nIt's moving faster!");
          }
          if (e.stateTimer % 120 < 80) {
            if (dist > 5) { e.x += (dx / dist) * e.speed; e.y += (dy / dist) * e.speed; }
          } else if (e.phase === 2 && e.stateTimer % 120 === 80) {
            const angle = Math.atan2(dy, dx);
            for (let i = -2; i <= 2; i++) {
              game.projectiles.push({
                x: e.x + e.w / 2, y: e.y + e.h / 2,
                vx: Math.cos(angle + i * 0.4) * 2.5, vy: Math.sin(angle + i * 0.4) * 2.5,
                w: 8, h: 8, damage: 1, color: C.bugLight,
              });
            }
            playSound('shoot');
          }
        } else {
          if (e.type === 'racecondition' && dist < 150) {
            const speed = e.speed * (0.5 + Math.random());
            e.x += (dx / dist) * speed; e.y += (dy / dist) * speed;
          } else if (dist < 120 && dist > 20) {
            e.x += (dx / dist) * e.speed; e.y += (dy / dist) * e.speed;
          } else if (e.stateTimer % 60 === 0) {
            e.wanderDir = Math.random() * Math.PI * 2;
          }
          if (e.wanderDir !== undefined && dist >= 120) {
            e.x += Math.cos(e.wanderDir) * e.speed * 0.5;
            e.y += Math.sin(e.wanderDir) * e.speed * 0.5;
          }
        }

        e.x = Math.max(TILE + 4, Math.min(e.x, (room.width - 1) * TILE - e.w - 4));
        e.y = Math.max(TILE + 4, Math.min(e.y, (room.height - 1) * TILE - e.h - 4));
        if (p.invincible === 0 && rectsOverlap(p, e)) damagePlayer(e.isBoss ? 2 : 1);
      });
    };

    const updateProjectiles = () => {
      const p = game.player;
      const room = ROOMS[game.room];
      game.projectiles = game.projectiles.filter(proj => {
        proj.x += proj.vx; proj.y += proj.vy;
        const tileX = Math.floor(proj.x / TILE);
        const tileY = Math.floor(proj.y / TILE);
        if (tileX < 0 || tileX >= room.width || tileY < 0 || tileY >= room.height) return false;
        if (room.tiles && room.tiles[tileY] && room.tiles[tileY][tileX] === 1) return false;
        if (p.invincible === 0 && rectsOverlap(p, proj)) { damagePlayer(proj.damage); return false; }
        return true;
      });
    };

    const updateParticles = () => {
      game.particles = game.particles.filter(part => {
        part.x += part.vx; part.y += part.vy; part.vy += 0.15; part.life--;
        return part.life > 0;
      });
    };

    const checkDoors = () => {
      const p = game.player;
      const room = ROOMS[game.room];
      if (!room.doors) return;
      room.doors.forEach((door: any) => {
        let doorRect;
        if (door.dir === 'up' || door.dir === 'down') {
          doorRect = { x: (door.x - 1) * TILE, y: door.y * TILE, w: TILE * 3, h: TILE };
        } else {
          doorRect = { x: door.x * TILE, y: (door.y - 1) * TILE, w: TILE, h: TILE * 3 };
        }
        if (rectsOverlap(p, doorRect)) {
          if (door.locked && !game.doorsUnlocked[`${game.room}_${door.to}`]) {
            if (game.enemies.length === 0) {
              game.doorsUnlocked[`${game.room}_${door.to}`] = true;
              showMessage("Door unlocked!");
            }
            return;
          }
          playSound('door');
          loadRoom(door.to, door.toX, door.toY);
        }
      });
    };

    const checkItems = () => {
      const p = game.player;
      game.items = game.items.filter(item => {
        if (rectsOverlap(p, item)) {
          game.roomsCleared[`${game.room}_${item.type}_${item.x}_${item.y}`] = true;
          switch (item.type) {
            case 'sword': p.hasSword = true; break;
            case 'coffee': p.hasCoffee = true; p.maxHealth += 4; p.health = p.maxHealth; break;
            case 'health': p.health = Math.min(p.health + 4, p.maxHealth); break;
          }
          playSound('item');
          if (item.message) showMessage(item.message);
          return false;
        }
        return true;
      });
    };

    const update = () => {
      if (game.state !== 'playing') return;
      game.frame++;
      updatePlayer();
      updateEnemies();
      updateProjectiles();
      updateParticles();
      checkDoors();
      checkItems();
      if (game.enemies.length === 0 && !game.roomsCleared[game.room]) game.roomsCleared[game.room] = true;
      if (game.shakeFrames > 0) game.shakeFrames--;
    };

    // Render functions
    const renderTitle = () => {
      ctx.fillStyle = C.darkGray;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      for (let i = 0; i < 8; i++) {
        ctx.fillStyle = i % 2 === 0 ? C.floor1 : C.floor2;
        ctx.fillRect(i * 32, HEIGHT - 32, 32, 32);
      }
      ctx.fillStyle = C.shirt;
      ctx.font = 'bold 28px "Courier New"';
      ctx.textAlign = 'center';
      ctx.fillText('DEBUG QUEST', WIDTH / 2, 55);
      ctx.fillStyle = C.text;
      ctx.font = '11px "Courier New"';
      ctx.fillText('A Monday Morning Adventure', WIDTH / 2, 78);
      drawBoss(WIDTH / 2 - 14, 95, 28, game.frame, false, 1);
      ctx.fillStyle = game.frame % 60 < 30 ? C.text : C.textDim;
      ctx.font = '14px "Courier New"';
      ctx.fillText('Press ENTER to Start', WIDTH / 2, 175);
      ctx.fillStyle = C.textDim;
      ctx.font = '10px "Courier New"';
      ctx.fillText('Arrow Keys: Move', WIDTH / 2, 200);
      ctx.fillText('Space: Attack', WIDTH / 2, 214);
    };

    const renderHUD = (roomName: string) => {
      const p = game.player;
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, WIDTH, 22);
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, 22); ctx.lineTo(WIDTH, 22);
      ctx.stroke();

      for (let i = 0; i < p.maxHealth / 2; i++) {
        const hx = 8 + i * 12;
        const hy = 6;
        ctx.fillStyle = C.healthEmpty;
        ctx.beginPath();
        ctx.moveTo(hx + 5, hy + 8);
        ctx.bezierCurveTo(hx, hy + 4, hx, hy, hx + 5, hy);
        ctx.bezierCurveTo(hx + 10, hy, hx + 10, hy + 4, hx + 5, hy + 8);
        ctx.fill();

        const healthAtHeart = Math.max(0, Math.min(2, p.health - i * 2));
        if (healthAtHeart > 0) {
          ctx.fillStyle = healthAtHeart === 2 ? C.healthFull : C.health;
          ctx.save();
          ctx.beginPath();
          ctx.rect(hx, hy, 10 * (healthAtHeart / 2), 10);
          ctx.clip();
          ctx.beginPath();
          ctx.moveTo(hx + 5, hy + 8);
          ctx.bezierCurveTo(hx, hy + 4, hx, hy, hx + 5, hy);
          ctx.bezierCurveTo(hx + 10, hy, hx + 10, hy + 4, hx + 5, hy + 8);
          ctx.fill();
          ctx.restore();
        }
      }

      ctx.fillStyle = C.textDim;
      ctx.font = '9px "Courier New"';
      ctx.textAlign = 'right';
      ctx.fillText(roomName, WIDTH - 8, 14);
      ctx.textAlign = 'left';
    };

    const renderGame = () => {
      const room = ROOMS[game.room];
      const p = game.player;
      const offsetX = (WIDTH - room.width * TILE) / 2;
      const offsetY = 24;

      ctx.save();
      ctx.translate(offsetX, offsetY);

      for (let y = 0; y < room.height; y++) {
        for (let x = 0; x < room.width; x++) {
          const tile = room.tiles[y][x];
          const px = x * TILE;
          const py = y * TILE;
          switch (tile) {
            case 0:
              ctx.fillStyle = C.floor1;
              ctx.fillRect(px, py, TILE, TILE);
              break;
            case 1:
              ctx.fillStyle = C.wall;
              ctx.fillRect(px, py, TILE, TILE);
              ctx.fillStyle = C.wallTop;
              ctx.fillRect(px, py, TILE, 5);
              ctx.fillStyle = C.wallHighlight;
              ctx.fillRect(px, py, TILE, 2);
              break;
            case 2:
              ctx.fillStyle = C.door;
              ctx.fillRect(px, py, TILE, TILE);
              ctx.fillStyle = C.doorHighlight;
              ctx.fillRect(px + 2, py + 2, TILE - 4, 3);
              break;
            case 3:
              ctx.fillStyle = C.floor2;
              ctx.fillRect(px, py, TILE, TILE);
              ctx.fillStyle = C.floor1;
              ctx.fillRect(px + 4, py + 4, 8, 8);
              break;
          }
        }
      }

      if (room.doors) {
        room.doors.forEach((door: any) => {
          if (door.locked && !game.doorsUnlocked[`${game.room}_${door.to}`]) {
            ctx.fillStyle = game.enemies.length > 0 ? '#8b0000' : C.success;
            ctx.beginPath();
            ctx.arc(door.x * TILE + 8, door.y * TILE + 8, 5, 0, Math.PI * 2);
            ctx.fill();
          }
        });
      }

      game.items.forEach(item => {
        switch (item.type) {
          case 'sword': drawSword(item.x, item.y, game.frame); break;
          case 'coffee': drawCoffee(item.x, item.y, game.frame); break;
          case 'health': drawHealth(item.x, item.y, game.frame); break;
        }
      });

      game.enemies.forEach(e => {
        if (e.isBoss) {
          drawBoss(e.x, e.y, e.w, game.frame, e.flash > 0, e.phase);
          const barWidth = 80;
          const barX = e.x + e.w / 2 - barWidth / 2;
          ctx.fillStyle = C.healthEmpty;
          ctx.fillRect(barX, e.y - 12, barWidth, 6);
          ctx.fillStyle = e.phase === 2 ? '#c0392b' : C.health;
          ctx.fillRect(barX + 1, e.y - 11, (barWidth - 2) * (e.hp / e.maxHp), 4);
        } else {
          switch (e.type) {
            case 'nullpointer': drawNullPointer(e.x, e.y, game.frame + e.stateTimer, e.flash > 0); break;
            case 'scopecreep': drawScopeCreep(e.x, e.y, game.frame + e.stateTimer, e.flash > 0); break;
            case 'racecondition': drawRaceCondition(e.x, e.y, game.frame + e.stateTimer, e.flash > 0); break;
          }
        }
      });

      game.projectiles.forEach(proj => {
        ctx.fillStyle = proj.color;
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, proj.w / 2, 0, Math.PI * 2);
        ctx.fill();
      });

      game.particles.forEach(part => {
        ctx.fillStyle = part.color;
        ctx.globalAlpha = part.life / 40;
        ctx.fillRect(part.x - part.size/2, part.y - part.size/2, part.size, part.size);
        ctx.globalAlpha = 1;
      });

      const flash = p.invincible > 0 && p.invincible % 8 < 4;
      if (!flash) drawPlayer(p.x, p.y, p.dir, p.moveFrame, p.attacking, p.attackFrame);

      ctx.restore();
      renderHUD(room.name);
    };

    const renderMessage = () => {
      if (!game.currentMessage) return;
      const lines = game.currentMessage.split('\n');
      const boxHeight = lines.length * 16 + 24;
      const boxY = HEIGHT - boxHeight - 16;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.92)';
      ctx.fillRect(16, boxY, WIDTH - 32, boxHeight);
      ctx.strokeStyle = C.shirt;
      ctx.lineWidth = 2;
      ctx.strokeRect(16, boxY, WIDTH - 32, boxHeight);
      ctx.fillStyle = C.text;
      ctx.font = '11px "Courier New"';
      ctx.textAlign = 'center';
      lines.forEach((line, i) => ctx.fillText(line, WIDTH / 2, boxY + 20 + i * 16));
      if (game.frame % 50 < 25) {
        ctx.fillStyle = C.textDim;
        ctx.fillText('▼', WIDTH / 2, boxY + boxHeight - 8);
      }
    };

    const renderGameOver = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      ctx.fillStyle = C.enemy;
      ctx.font = 'bold 22px "Courier New"';
      ctx.textAlign = 'center';
      ctx.fillText('SEGMENTATION FAULT', WIDTH / 2, HEIGHT / 2 - 30);
      ctx.fillStyle = C.text;
      ctx.font = '12px "Courier New"';
      ctx.fillText('The bugs got you.', WIDTH / 2, HEIGHT / 2);
      ctx.fillStyle = game.frame % 60 < 30 ? C.text : C.textDim;
      ctx.fillText('Press ENTER to try again', WIDTH / 2, HEIGHT / 2 + 35);
    };

    const renderVictory = () => {
      ctx.fillStyle = C.darkGray;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      for (let i = 0; i < 30; i++) {
        ctx.fillStyle = [C.shirt, C.item, C.success, C.bugLight, C.healthFull][i % 5];
        const x = (i * 37 + game.frame * 0.7) % WIDTH;
        const y = (i * 23 + game.frame * 0.4) % HEIGHT;
        ctx.fillRect(x, y, 3 + (i % 3), 3 + (i % 3));
      }
      ctx.fillStyle = C.success;
      ctx.font = 'bold 24px "Courier New"';
      ctx.textAlign = 'center';
      ctx.fillText('BUG SQUASHED!', WIDTH / 2, 45);
      ctx.fillStyle = C.text;
      ctx.font = '12px "Courier New"';
      ctx.fillText('Production is saved.', WIDTH / 2, 72);
      ctx.fillText('Time for coffee.', WIDTH / 2, 90);
      ctx.fillStyle = C.shirt;
      ctx.font = '10px "Courier New"';
      ctx.fillText('━━━━━━━━━━━━━━━━━━━', WIDTH / 2, 115);
      ctx.fillStyle = C.text;
      ctx.fillText('Thanks for playing!', WIDTH / 2, 138);
      ctx.fillText("Connect with Joe:", WIDTH / 2, 156);
      ctx.fillStyle = C.item;
      ctx.font = '12px "Courier New"';
      ctx.fillText('j.hamilton@servetdigital.com', WIDTH / 2, 182);
      ctx.fillText('linkedin.com/in/jhamilton01', WIDTH / 2, 200);
      ctx.fillStyle = C.textDim;
      ctx.font = '9px "Courier New"';
      ctx.fillText("Let's debug something together.", WIDTH / 2, 222);
      ctx.fillStyle = game.frame % 60 < 30 ? C.text : C.textDim;
      ctx.font = '11px "Courier New"';
      ctx.fillText('Press ENTER to play again', WIDTH / 2, HEIGHT - 16);
    };

    const render = () => {
      ctx.save();
      ctx.scale(SCALE, SCALE);
      if (game.shakeFrames > 0) {
        ctx.translate((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6);
      }
      ctx.fillStyle = C.black;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      switch (game.state) {
        case 'title': renderTitle(); break;
        case 'playing': case 'message': renderGame(); if (game.state === 'message') renderMessage(); break;
        case 'gameover': renderGame(); renderGameOver(); break;
        case 'victory': renderVictory(); break;
      }
      ctx.restore();
    };

    // Input handling
    const handleKeyDown = (e: KeyboardEvent) => {
      initAudio();
      keysRef.current[e.code] = true;

      if (game.state === 'title' && (e.code === 'Enter' || e.code === 'Space')) startGame();
      if (game.state === 'message' && (e.code === 'Enter' || e.code === 'Space')) {
        game.currentMessage = null;
        game.state = 'playing';
      }
      if (game.state === 'victory' && (e.code === 'Enter' || e.code === 'Space')) game.state = 'title';
      if (game.state === 'gameover' && (e.code === 'Enter' || e.code === 'Space')) startGame();
      if (e.code === 'Escape') onClose();

      e.preventDefault();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.code] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Game loop
    let lastTime = 0;
    const frameTime = 1000 / 60;

    const gameLoop = (timestamp: number) => {
      const delta = timestamp - lastTime;
      if (delta >= frameTime) {
        lastTime = timestamp - (delta % frameTime);
        update();
        game.frame++;
      }
      render();
      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close();
    };
  }, [initAudio, playSound, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative"
          >
            <button
              onClick={onClose}
              className="absolute -top-10 right-0 text-gray-400 hover:text-white transition-colors text-sm"
            >
              Press ESC to close
            </button>
            <canvas
              ref={canvasRef}
              width={512}
              height={448}
              style={{
                display: 'block',
                imageRendering: 'pixelated',
                border: '4px solid #333',
                borderRadius: '4px',
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DebugQuestGame;